import { usePopUpMessage } from '@hb/react'
import {
  ICD10Admin,
  ICD10GroupAdmin,
  ICD10LibraryData,
  ICD_CODE_LIBRARY,
  icdCodeGroups,
  objectToArray,
  reorder,
  SETTINGS,
  slugify,
  UpdateCallback,
  WithId,
} from '@hb/shared'
import { OnDragEndResponder } from '@hello-pangea/dnd'
import { deleteField, DocumentReference, writeBatch } from 'firebase/firestore'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { db } from '../../../../backend/db/db'
import { useDocument } from '../../../../backend/db/hooks'
import { useUpdateDoc } from '../../../../hooks'

export interface ICD10GroupWithId extends ICD10GroupAdmin {
  id: string
}

export interface UseICD10Library {
  data: ICD10LibraryData | null
  loading: boolean
  ref: DocumentReference<ICD10LibraryData> | null
  groups: ICD10GroupWithId[]
  handleAddCode: (groupId: string, code: ICD10Admin) => Promise<UpdateCallback>
  handleUpdateCodeDescription: (
    groupId: string,
    codeKey: string,
    description: string,
  ) => Promise<UpdateCallback>
  handleUpdateCodeKeywords: (
    groupId: string,
    codeKey: string,
    keywords: string[],
  ) => Promise<UpdateCallback>
  handleUpdateCodeAutoTrigger: (
    groupId: string,
    codeKey: string,
    autoTrigger: string,
  ) => Promise<UpdateCallback>
  handleDeleteCode: (groupId: string, codeKey: string) => Promise<UpdateCallback>
  handleCodeDragEnd: (groupId: string) => OnDragEndResponder
  handleAddGroup: (name: string) => Promise<UpdateCallback>
  handleUpdateGroupName: (groupId: string, name: string) => Promise<UpdateCallback>
  handleDeleteGroup: (groupId: string) => Promise<UpdateCallback>
  handleGroupDragEnd: OnDragEndResponder
}

const buildGroupsFromHardcoded = (): ICD10LibraryData['groups'] => {
  const groups: ICD10LibraryData['groups'] = {}
  icdCodeGroups.forEach((g, gi) => {
    const gid = slugify(g.name)
    const codes: Record<string, ICD10Admin> = {}
    g.codes.forEach((c, ci) => {
      codes[c.code] = {
        code: c.code,
        description: c.description,
        keywords: c.keywords ?? [],
        rank: ci,
      }
    })
    groups[gid] = { name: g.name, rank: gi, codes }
  })
  return groups
}

export const useICD10Library = (): UseICD10Library => {
  const { data, loading, ref } = useDocument<ICD10LibraryData>(SETTINGS, ICD_CODE_LIBRARY)
  const { showError } = usePopUpMessage()
  const update = useUpdateDoc('icd10library')

  // Seed Firestore on first load from hardcoded library
  const seeded = useRef(false)
  useEffect(() => {
    if (!loading && !data && ref && !seeded.current) {
      seeded.current = true
      const groups = buildGroupsFromHardcoded()
      update(ref, 'groups', groups)
    }
  }, [loading, data, ref, update])

  const serverGroups: ICD10GroupWithId[] = useMemo(() => {
    if (!data?.groups) return []
    return objectToArray(data.groups).sort((a, b) => (a.rank ?? 0) - (b.rank ?? 0))
  }, [data])

  const [groups, setGroups] = useState(serverGroups)
  const savePending = useRef(false)

  useEffect(() => {
    if (!savePending.current) {
      setGroups(serverGroups)
    }
  }, [serverGroups])

  const handleAddCode = useCallback(
    async (groupId: string, code: ICD10Admin): Promise<UpdateCallback> => {
      if (!ref) return { error: 'No ref when adding code' }
      const group = data?.groups?.[groupId]
      if (!group) return { error: 'Group not found' }
      if (group.codes[code.code])
        return { error: `Code ${code.code} already exists in this category` }
      const rank = Object.keys(group.codes).length
      return update(ref, `groups.${groupId}.codes.${code.code}`, { ...code, rank })
    },
    [ref, data, update],
  )

  const handleUpdateCodeDescription = useCallback(
    async (groupId: string, codeKey: string, description: string): Promise<UpdateCallback> =>
      update(ref, `groups.${groupId}.codes.${codeKey}.description`, description),
    [ref, update],
  )

  const handleUpdateCodeKeywords = useCallback(
    async (groupId: string, codeKey: string, keywords: string[]): Promise<UpdateCallback> =>
      update(ref, `groups.${groupId}.codes.${codeKey}.keywords`, keywords),
    [ref, update],
  )

  const handleUpdateCodeAutoTrigger = useCallback(
    async (groupId: string, codeKey: string, autoTrigger: string): Promise<UpdateCallback> =>
      update(ref, `groups.${groupId}.codes.${codeKey}.autoTrigger`, autoTrigger),
    [ref, update],
  )

  const handleDeleteCode = useCallback(
    async (groupId: string, codeKey: string): Promise<UpdateCallback> =>
      update(ref, `groups.${groupId}.codes.${codeKey}`, deleteField()),
    [ref, update],
  )

  const handleCodeDragEnd = useCallback(
    (groupId: string): OnDragEndResponder =>
      async result => {
        const { source, destination } = result
        if (!destination || source.index === destination.index) return
        if (!ref) return
        const group = data?.groups?.[groupId]
        if (!group) return
        const sortedCodes: WithId<ICD10Admin>[] = objectToArray(group.codes).sort(
          (a, b) => a.rank - b.rank,
        )
        const reordered = reorder(sortedCodes, result)
        const batch = writeBatch(db)
        reordered.forEach((item, idx) => {
          const original = sortedCodes.find(c => c.id === item.id)
          if (original?.rank !== idx) {
            batch.update(ref, `groups.${groupId}.codes.${item.id}.rank`, idx)
          }
        })
        await batch.commit()
      },
    [data, ref],
  )

  const handleAddGroup = useCallback(
    async (name: string): Promise<UpdateCallback> => {
      if (!ref) return { error: 'No ref when adding group' }
      const newId = slugify(name)
      if (!newId) return { error: 'Category name is required' }
      if (groups.some(g => g.id === newId)) {
        showError(`Category "${name}" already exists`)
        return { error: `Category "${name}" already exists` }
      }
      return update(ref, `groups.${newId}`, { name, rank: groups.length, codes: {} })
    },
    [ref, groups, showError, update],
  )

  const handleUpdateGroupName = useCallback(
    async (groupId: string, name: string): Promise<UpdateCallback> => {
      if (!ref) return { error: 'No ref when updating group name' }
      const newId = slugify(name)
      const existing = data?.groups?.[groupId]
      if (!existing) return { error: 'Group not found' }
      if (groups.some(g => g.id === newId && g.id !== groupId)) {
        showError(`Category "${name}" already exists`)
        return { error: `Category "${name}" already exists` }
      }
      const batch = writeBatch(db)
      batch.update(ref, `groups.${newId}`, { ...existing, name })
      if (newId !== groupId) {
        batch.update(ref, `groups.${groupId}`, deleteField())
      }
      return batch
        .commit()
        .then(() => ({ success: 'Category name updated' }))
        .catch(err => ({ error: `Error updating category name: ${err.message}` }))
    },
    [ref, data, groups, showError],
  )

  const handleDeleteGroup = useCallback(
    async (groupId: string): Promise<UpdateCallback> =>
      update(ref, `groups.${groupId}`, deleteField()),
    [ref, update],
  )

  const handleGroupDragEnd: OnDragEndResponder = useCallback(
    async result => {
      const { source, destination } = result
      if (!destination || source.index === destination.index) return
      if (!ref) return
      const reordered = reorder(groups, result)
      setGroups(reordered)
      savePending.current = true
      const batch = writeBatch(db)
      reordered.forEach((item, idx) => {
        const originalRank = groups.find(g => g.id === item.id)?.rank
        if (originalRank !== idx) {
          batch.update(ref, `groups.${item.id}.rank`, idx)
        }
      })
      await batch.commit()
      savePending.current = false
    },
    [groups, ref],
  )

  return {
    data,
    loading,
    ref,
    groups,
    handleAddCode,
    handleUpdateCodeDescription,
    handleUpdateCodeKeywords,
    handleUpdateCodeAutoTrigger,
    handleDeleteCode,
    handleCodeDragEnd,
    handleAddGroup,
    handleUpdateGroupName,
    handleDeleteGroup,
    handleGroupDragEnd,
  }
}
